/** AdminAcquisition - owner-acquisition funnel: sales traffic → signup → onboarding → activation → first revenue. */
import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity } from 'react-native';
import { Redirect } from 'expo-router';

import { api } from '../../src/api/client';
import { useAdminPermissions } from '../../src/hooks/useAdminPermissions';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import StageBadge from '../../src/components/StageBadge';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

const MOBILE_BREAKPOINT = 768;

interface Stage { key: string; label: string; count: number }
interface TrafficSource { source: string; visitors: number; pageviews: number }
interface OwnerRow {
  id: string; businessName: string; email: string; createdAt: string;
  stripeOnboarded: boolean; hasLocation: boolean; hasController: boolean;
  activated: boolean; activatedAt: string | null;
  firstRevenueAt: string | null; daysToFirstRevenue: number | null;
  referralCode: string | null; referralName: string | null;
}
interface AcquisitionData {
  period: { from: string; to: string; days: number };
  traffic: { pageviews: number; visitors: number; sources: TrafficSource[] };
  stages: Stage[];
  owners: OwnerRow[];
  total: number;
  page: number;
  limit: number;
}

const PERIOD_OPTIONS = [
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
  { label: '180d', value: 180 },
  { label: '1y', value: 365 },
];

const fmtDateTime = (d: string) => new Date(d).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

export default function AdminAcquisition() {
  const { has, loaded } = useAdminPermissions();
  if (loaded && !has('platform_acquisition')) return <Redirect href="/admin/users" />;
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < MOBILE_BREAKPOINT;
  const styles = useStyles(colors, isMobile);

  const [data, setData] = useState<AcquisitionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [days, setDays] = useState(90);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (d = 90, p = 1, s = '', signal?: AbortSignal) => {
    setRowsLoading(true);
    try {
      const params = new URLSearchParams({ days: String(d), page: String(p), limit: '10' });
      if (s) params.set('search', s);
      const res = await api<AcquisitionData>(`/api/admin/acquisition?${params}`);
      if (signal?.aborted) return;
      setData(res);
      setPage(p);
    } catch (err) { if (!signal?.aborted) console.error(err); }
    finally { if (!signal?.aborted) { setLoading(false); setRowsLoading(false); } }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchData(days, 1, search, ac.signal);
    return () => ac.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  if (loading || !data) {
    return <View style={styles.center}>{loading
      ? <ActivityIndicator size="large" color={colors.primary} />
      : <Text style={styles.empty}>Failed to load acquisition data</Text>}</View>;
  }

  const maxStage = Math.max(1, ...data.stages.map(s => s.count));
  const csvEndpoint = `/api/admin/acquisition?${new URLSearchParams({ days: String(days), ...(search && { search }) })}`;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer role="admin">

        {/* Period selector */}
        <View style={styles.periodRow}>
          {PERIOD_OPTIONS.map(opt => (
            <TouchableOpacity
              key={opt.value}
              style={[styles.periodChip, days === opt.value && styles.periodChipActive]}
              onPress={() => setDays(opt.value)}
            >
              <Text style={[styles.periodChipText, days === opt.value && styles.periodChipTextActive]}>{opt.label}</Text>
            </TouchableOpacity>
          ))}
          <Text style={styles.periodMeta}>
            {new Date(data.period.from).toLocaleDateString()} – {new Date(data.period.to).toLocaleDateString()}
          </Text>
        </View>

        {/* Traffic */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Sales Site Traffic</Text>
          <View style={styles.trafficTotals}>
            <View style={styles.trafficStat}>
              <Text style={styles.trafficValue}>{data.traffic.visitors.toLocaleString()}</Text>
              <Text style={styles.trafficLabel}>Unique Visitors</Text>
            </View>
            <View style={styles.trafficStat}>
              <Text style={styles.trafficValue}>{data.traffic.pageviews.toLocaleString()}</Text>
              <Text style={styles.trafficLabel}>Pageviews</Text>
            </View>
          </View>
          {data.traffic.sources.length > 0 && (
            <View style={styles.sourceList}>
              {data.traffic.sources.map(s => (
                <View key={s.source} style={styles.sourceRow}>
                  <Text style={styles.sourceName} numberOfLines={1}>{s.source}</Text>
                  <Text style={styles.sourceCount}>{s.visitors.toLocaleString()} visitors · {s.pageviews.toLocaleString()} views</Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Funnel */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Owner Funnel ({data.period.days}d)</Text>
          {data.stages.map(stage => {
            const pct = Math.max(stage.count > 0 ? 4 : 0, Math.round((stage.count / maxStage) * 100));
            return (
              <View key={stage.key} style={styles.funnelRow}>
                <Text style={styles.funnelLabel} numberOfLines={1}>{stage.label}</Text>
                <View style={styles.funnelBarTrack}>
                  <View style={[styles.funnelBar, { width: `${pct}%` }]} />
                </View>
                <Text style={styles.funnelCount}>{stage.count.toLocaleString()}</Text>
              </View>
            );
          })}
        </View>

        {/* Owner table */}
        <Text style={styles.sectionTitle}>Owners in Period</Text>
        <View style={styles.card}>
          <AdminToolbar
            search={search}
            onSearchChange={setSearch}
            onSearch={() => fetchData(days, 1, search)}
            total={data.total}
            page={page}
            limit={10}
            onPageChange={(p) => fetchData(days, p, search)}
            csvEndpoint={csvEndpoint}
            csvFilename="acquisition.csv"
            label="owner"
          />
          {rowsLoading ? (
            <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: spacing.lg }} />
          ) : data.owners.length === 0 ? (
            <Text style={styles.empty}>No owners found</Text>
          ) : (
            data.owners.map(o => (
              <View key={o.id} style={styles.listRow}>
                <View style={styles.listBody}>
                  <Text style={styles.listName} numberOfLines={1}>{o.businessName}</Text>
                  <Text style={styles.listSub} numberOfLines={1}>{o.email}</Text>
                  <Text style={styles.listMeta} numberOfLines={1}>Signed up {fmtDateTime(o.createdAt)}</Text>
                  {o.referralCode ? (
                    <Text style={styles.listMeta} numberOfLines={1}>Referred by {o.referralName} ({o.referralCode})</Text>
                  ) : null}
                  <View style={styles.badgeRow}>
                    <StageBadge label="Stripe" on={o.stripeOnboarded} />
                    <StageBadge label="Location" on={o.hasLocation} />
                    <StageBadge label="Controller" on={o.hasController} />
                    <StageBadge label="Activated" on={o.activated} />
                    <StageBadge label="Revenue" on={!!o.firstRevenueAt} />
                  </View>
                </View>
                <View style={styles.listRight}>
                  {o.firstRevenueAt ? (
                    <>
                      <Text style={styles.daysValue}>{o.daysToFirstRevenue}d</Text>
                      <Text style={styles.daysLabel}>to first revenue</Text>
                      <Text style={styles.daysMeta}>{fmtDateTime(o.firstRevenueAt)}</Text>
                    </>
                  ) : (
                    <Text style={styles.daysNone}>no revenue yet</Text>
                  )}
                </View>
              </View>
            ))
          )}
        </View>

      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: isMobile ? spacing.md : spacing.lg },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.sm, marginTop: spacing.md },
  empty: { textAlign: 'center', color: c.textSecondary, paddingVertical: spacing.md, fontSize: fontSize.sm },

  periodRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.md, flexWrap: 'wrap' },
  periodChip: {
    paddingHorizontal: spacing.sm, paddingVertical: 6,
    borderRadius: borderRadius.sm, backgroundColor: c.surface,
    borderWidth: 1, borderColor: c.borderLight,
  },
  periodChipActive: { backgroundColor: c.primaryBg, borderColor: c.primary },
  periodChipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  periodChipTextActive: { color: c.primary, fontWeight: fontWeight.semibold },
  periodMeta: { fontSize: fontSize.xs, color: c.textLight, marginLeft: spacing.xs },

  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.lg,
    padding: spacing.md, marginBottom: spacing.md,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1,
  },
  cardTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },

  trafficTotals: { flexDirection: 'row', gap: spacing.xl, marginBottom: spacing.sm },
  trafficStat: {},
  trafficValue: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, fontVariant: ['tabular-nums'] },
  trafficLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  sourceList: { borderTopWidth: 1, borderTopColor: c.borderLight, paddingTop: spacing.xs },
  sourceRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6, gap: spacing.sm },
  sourceName: { fontSize: fontSize.sm, color: c.text, flex: 1 },
  sourceCount: { fontSize: fontSize.xs, color: c.textSecondary, fontVariant: ['tabular-nums'] },

  funnelRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, gap: spacing.sm },
  funnelLabel: { width: isMobile ? 110 : 160, fontSize: fontSize.xs, color: c.textSecondary },
  funnelBarTrack: { flex: 1, height: 18, backgroundColor: c.background, borderRadius: borderRadius.sm, overflow: 'hidden' },
  funnelBar: { height: '100%', backgroundColor: c.primary, borderRadius: borderRadius.sm },
  funnelCount: { width: 48, textAlign: 'right', fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, fontVariant: ['tabular-nums'] },

  listRow: {
    flexDirection: 'row', alignItems: 'flex-start',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: spacing.sm,
  },
  listBody: { flex: 1, minWidth: 0 },
  listName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  listSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  listMeta: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  badgeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 6 },
  listRight: { alignItems: 'flex-end', minWidth: 90 },
  daysValue: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text, fontVariant: ['tabular-nums'] },
  daysLabel: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 1 },
  daysMeta: { fontSize: 10, color: c.textLight, marginTop: 2, textAlign: 'right' },
  daysNone: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
